/**
 * Validation utilities for AI-generated interactive lesson JSON
 */

/**
 * Validates a lesson screen object
 * @param {Object} screen - Screen object to validate
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validateScreen = (screen) => {
  const errors = [];

  if (!screen) {
    return { valid: false, errors: ['Screen is required'] };
  }

  if (!screen.id || typeof screen.id !== 'string') {
    errors.push('Screen must have a valid id (string)');
  }

  if (!screen.type || !['content', 'question-mcq', 'question-open', 'coding', 'activity', 'reflection'].includes(screen.type)) {
    errors.push('Screen must have a valid type');
  }

  if (!screen.title || typeof screen.title !== 'string') {
    errors.push('Screen must have a valid title (string)');
  }

  if (screen.contentForTeacherOrSystem === undefined) {
    errors.push('Screen must have contentForTeacherOrSystem');
  }

  // Validate uiLayoutHints if present
  if (screen.uiLayoutHints) {
    if (typeof screen.uiLayoutHints !== 'object') {
      errors.push('uiLayoutHints must be an object');
    }
  }

  // Validate checkingLogic if present
  if (screen.checkingLogic) {
    if (typeof screen.checkingLogic !== 'object') {
      errors.push('checkingLogic must be an object');
    } else {
      if (screen.checkingLogic.acceptableAnswers && !Array.isArray(screen.checkingLogic.acceptableAnswers)) {
        errors.push('checkingLogic.acceptableAnswers must be an array');
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Validates the complete lesson data structure
 * @param {Object} lessonData - Lesson data object to validate
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validateLessonData = (lessonData) => {
  const errors = [];

  if (!lessonData) {
    return { valid: false, errors: ['Lesson data is required'] };
  }

  if (!lessonData.lessonTitle || typeof lessonData.lessonTitle !== 'string') {
    errors.push('Lesson must have a valid lessonTitle (string)');
  }

  if (!lessonData.learningObjectives || !Array.isArray(lessonData.learningObjectives)) {
    errors.push('Lesson must have learningObjectives (array)');
  }

  if (!lessonData.screens || !Array.isArray(lessonData.screens)) {
    errors.push('Lesson must have screens (array)');
  } else {
    // Validate each screen
    lessonData.screens.forEach((screen, index) => {
      const screenValidation = validateScreen(screen);
      if (!screenValidation.valid) {
        errors.push(`Screen ${index + 1} (${screen.id || 'unknown'}): ${screenValidation.errors.join(', ')}`);
      }
    });
  }

  // Validate summaryScreen if present
  if (lessonData.summaryScreen) {
    if (typeof lessonData.summaryScreen !== 'object') {
      errors.push('summaryScreen must be an object');
    } else {
      if (lessonData.summaryScreen.keyTakeaways && !Array.isArray(lessonData.summaryScreen.keyTakeaways)) {
        errors.push('summaryScreen.keyTakeaways must be an array');
      }
      if (lessonData.summaryScreen.suggestedNextSteps && !Array.isArray(lessonData.summaryScreen.suggestedNextSteps)) {
        errors.push('summaryScreen.suggestedNextSteps must be an array');
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Normalizes a lesson data object, ensuring all required fields exist with defaults
 * @param {Object} lessonData - Lesson data to normalize
 * @returns {Object} - Normalized lesson data
 */
export const normalizeLessonData = (lessonData) => {
  if (!lessonData) {
    throw new Error('Lesson data is required');
  }

  const normalized = {
    lessonTitle: lessonData.lessonTitle || 'Untitled Lesson',
    learningObjectives: Array.isArray(lessonData.learningObjectives) ? lessonData.learningObjectives : [],
    screens: Array.isArray(lessonData.screens) ? lessonData.screens.map((screen, index) => ({
      id: screen.id || `screen-${index + 1}`,
      type: screen.type || 'content',
      title: screen.title || `Step ${index + 1}`,
      studentActionDescription: screen.studentActionDescription || '',
      contentForTeacherOrSystem: screen.contentForTeacherOrSystem || '',
      examples: Array.isArray(screen.examples) ? screen.examples : [],
      reflectionQuestion: screen.reflectionQuestion || '',
      uiLayoutHints: {
        topBar: screen.uiLayoutHints?.topBar || `Step ${index + 1} of ${lessonData.screens.length}`,
        leftPanel: screen.uiLayoutHints?.leftPanel || '',
        rightPanel: screen.uiLayoutHints?.rightPanel || '',
        primaryButtonLabel: screen.uiLayoutHints?.primaryButtonLabel || 'Next',
        secondaryButtonLabel: screen.uiLayoutHints?.secondaryButtonLabel || ''
      },
      checkingLogic: {
        expectedAnswer: screen.checkingLogic?.expectedAnswer || '',
        acceptableAnswers: Array.isArray(screen.checkingLogic?.acceptableAnswers) 
          ? screen.checkingLogic.acceptableAnswers 
          : [],
        evaluationGuidelines: screen.checkingLogic?.evaluationGuidelines || '',
        feedbackCorrect: screen.checkingLogic?.feedbackCorrect || '',
        feedbackIncorrect: screen.checkingLogic?.feedbackIncorrect || ''
      }
    })) : [],
    summaryScreen: {
      keyTakeaways: Array.isArray(lessonData.summaryScreen?.keyTakeaways) 
        ? lessonData.summaryScreen.keyTakeaways 
        : [],
      suggestedNextSteps: Array.isArray(lessonData.summaryScreen?.suggestedNextSteps) 
        ? lessonData.summaryScreen.suggestedNextSteps 
        : []
    }
  };

  return normalized;
};

/**
 * Type definitions for TypeScript-like documentation
 */
export const LessonScreenTypes = {
  CONTENT: 'content',
  QUESTION_MCQ: 'question-mcq',
  QUESTION_OPEN: 'question-open',
  CODING: 'coding',
  ACTIVITY: 'activity',
  REFLECTION: 'reflection'
};
